import React from "react";
import styles from "./toggle-tile.module.css";
import { withSafeRender } from "../safe-render";

/**
 * ToggleTile — one on/off tile in a pick-several grid, announced as
 * `aria-pressed`. No state and no handler: the picker owns the selection and
 * reads `button.value` from one delegated listener on its grid.
 */
export interface ToggleTileProps {
  /** Visible label and accessible name. A blank label renders nothing at all. */
  label: string;
  /** Pressed state; renders aria-pressed. Defaults to false. */
  selected?: boolean;
  /** Written to the button's `value` attribute so a parent can identify the tile from a delegated click. Absent, no attribute. */
  value?: string;
  /** Native disabled. Absent, the tile is live. */
  disabled?: boolean;
}

/* The frame's own check asset, in currentColor so forced colours repaint it. */
const CHECK_GLYPH_PATH =
  "M9.5923 18.4104C9.81569 18.6538 10.1396 18.7203 10.4195 18.6239H10.422C10.425 " +
  "18.6229 10.4292 18.6217 10.4339 18.6199L10.4352 18.6193C10.4758 18.6043 10.5119 " +
  "18.5857 10.5446 18.5675L10.5452 18.5682C10.5452 18.5682 10.5476 18.5676 10.549 " +
  "18.5669C10.5541 18.5642 10.566 18.5575 10.5798 18.549L10.581 18.5483C10.6241 " +
  "18.5212 10.6779 18.4834 10.73 18.4303C10.7387 18.4213 10.7458 18.4129 10.7507 " +
  "18.4071C10.7554 18.4014 10.7592 18.3965 10.7608 18.3945L10.762 18.3931L10.7646 " +
  "18.3898L10.7671 18.3872C10.7709 18.3827 10.7768 18.3754 10.7838 18.3667L10.7865 " +
  "18.3633L10.7891 18.3606C10.7903 18.3592 10.7919 18.3566 10.7941 18.354C10.7984 " +
  "18.3489 10.805 18.3409 10.8123 18.3308L19.1696 6.69434C19.421 6.3442 19.3761 " +
  "5.85422 19.0778 5.561L19.0143 5.5053C18.6604 5.22235 18.1558 5.29547 17.8873 " +
  "5.6691L10.0951 16.517L6.04913 12.1057C5.75952 11.7899 5.29388 11.7626 4.97367 " +
  "12.0288L4.91145 12.0858C4.59219 12.4118 4.58367 12.9492 4.89259 13.2861L9.5923 " +
  "18.4104Z";

function ToggleTileImpl({
  label,
  selected = false,
  value,
  disabled = false
}: ToggleTileProps): JSX.Element | null {
  /* A CMS row with no label would otherwise ship a nameless control. */
  if (typeof label !== "string" || label.trim() === "") {
    return null;
  }

  const pressed = selected === true;

  return (
    <button
      aria-pressed={pressed}
      className={styles.tile}
      disabled={disabled === true}
      type="button"
      value={value}
    >
      <span className={`dds-text dds-body-2 dds-weight-medium ${styles.label}`}>
        {label}
      </span>
      {pressed ? (
        <span aria-hidden="true" className={styles.check}>
          <svg
            className={styles.glyph}
            focusable="false"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d={CHECK_GLYPH_PATH} fill="currentColor" />
          </svg>
        </span>
      ) : null}
    </button>
  );
}
export const ToggleTile = withSafeRender(ToggleTileImpl, "ToggleTile");

export default ToggleTile;
